import type { TrackReferenceOrPlaceholder } from '@livekit/components-react';

/**
 * Pure helpers over a LiveKit track reference.
 *
 * They live outside the tile components so the components stay
 * component-only (fast refresh) and so the grid and the overlay agree on one
 * definition of a tile's key and of "has video".
 */

/**
 * Stable, unique key for one tile.
 *
 * Identity is the user id minted into the token, and a participant can appear
 * once per source (camera and screen share), so the pair is unique per tile.
 * `sid` is deliberately not used: it is empty until the participant is fully
 * connected, which would collide across tiles on the first render.
 */
export function tileKey(trackRef: TrackReferenceOrPlaceholder): string {
  return `${trackRef.participant.identity}:${trackRef.source}`;
}

/**
 * True when the reference carries a track that is actually painting pixels.
 *
 * A placeholder has no publication at all, and a published-but-muted camera
 * has one whose track renders nothing, so both fall back to the avatar.
 */
export function hasLiveVideo(trackRef: TrackReferenceOrPlaceholder): boolean {
  return Boolean(trackRef.publication?.track) && !trackRef.publication?.isMuted;
}

/**
 * Best display name for a participant: the username the token carries, with
 * the identity (the user id) as a last resort so a tile is never nameless.
 */
export function displayNameOf(trackRef: TrackReferenceOrPlaceholder): string {
  return trackRef.participant.name?.trim() || trackRef.participant.identity;
}

/**
 * Character id carried in the participant metadata, when there is one.
 *
 * The backend mints the token metadata as `{ status, preferences, avatar }`,
 * where `avatar` is the user's character id (`User.avatar`, e.g.
 * `premade_01`) read from the database at mint time — the same id
 * `PlayerAvatar` takes. It is absent when the lookup failed, and
 * `PlayerAvatar` then falls back to its own generic avatar.
 *
 * Like the rest of the metadata it is frozen when the token is minted: a
 * character picked mid-call shows from the next token, i.e. the next time the
 * participant enters the area.
 */
export function avatarIdOf(metadata: string | undefined): string | undefined {
  if (!metadata) return undefined;

  try {
    const parsed: unknown = JSON.parse(metadata);
    if (parsed && typeof parsed === 'object' && 'avatar' in parsed) {
      const avatar = (parsed as { avatar?: unknown }).avatar;
      return typeof avatar === 'string' ? avatar : undefined;
    }
  } catch {
    // Metadata is opaque to us; a tile is not worth a thrown render.
  }

  return undefined;
}
